import { useRef } from "react";
import { Camera, ImagePlus, Video, X } from "lucide-react";

export default function MediaCapture({ media = [], onChange }) {
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  const videoInput = useRef(null);

  const handleFiles = (files, kind) => {
    if (!files || !files.length) return;
    const items = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: kind === "video" ? "video" : "image",
      name: file.name,
      url: URL.createObjectURL(file),
    }));
    onChange?.([...media, ...items]);
  };

  const remove = (id) => onChange?.(media.filter((m) => m.id !== id));

  const trigger = (ref) => ref.current?.click();

  const btnBase =
    "flex flex-col items-center justify-center h-20 rounded-sm bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:border-orange-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors gap-1";
  const labelBase =
    "font-mono-tech text-[9px] uppercase tracking-widest text-zinc-700 dark:text-zinc-300";

  return (
    <div className="space-y-3" data-testid="media-capture">
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => trigger(cameraInput)}
          className={btnBase}
          data-testid="media-camera-btn"
        >
          <Camera className="w-5 h-5 text-orange-500" />
          <span className={labelBase}>Cámara</span>
        </button>
        <button
          type="button"
          onClick={() => trigger(galleryInput)}
          className={btnBase}
          data-testid="media-gallery-btn"
        >
          <ImagePlus className="w-5 h-5 text-orange-500" />
          <span className={labelBase}>Galería</span>
        </button>
        <button
          type="button"
          onClick={() => trigger(videoInput)}
          className={btnBase}
          data-testid="media-video-btn"
        >
          <Video className="w-5 h-5 text-orange-500" />
          <span className={labelBase}>Vídeo</span>
        </button>
      </div>

      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, "image")}
        data-testid="media-camera-input"
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, "image")}
        data-testid="media-gallery-input"
      />
      <input
        ref={videoInput}
        type="file"
        accept="video/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, "video")}
        data-testid="media-video-input"
      />

      {media.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="media-grid">
          {media.map((m) => (
            <div
              key={m.id}
              className="relative aspect-square rounded-sm overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 group"
              data-testid={`media-item-${m.type}`}
            >
              {m.type === "video" ? (
                <video src={m.url} className="w-full h-full object-cover" muted />
              ) : (
                <img src={m.url} alt={m.name} className="w-full h-full object-cover" />
              )}
              {m.type === "video" && (
                <div className="absolute top-1 left-1 bg-black/70 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                  <Video className="w-2.5 h-2.5 text-orange-500" />
                  <span className="font-mono-tech text-[8px] text-white uppercase">VID</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => remove(m.id)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-red-500 transition-colors"
                data-testid="media-remove-btn"
                aria-label="Eliminar"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
